import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseHttpService } from '../../../shared/services/base-http.service';
import { CONTROLLER_CONSTANTS } from '../../../shared/constants/controller.constants';
import { ENDPOINT_CONSTANTS } from '../../../shared/constants/endpoint.constants';
import {
  ApplyLeavePayload,
  LeaveBalance,
  LeaveRequest,
  LeaveSystemRoleInfo
} from '../interfaces/leave.interface';

interface ApiResponse<T> {
  isSuccess?: boolean;
  message?: string;
  data?: T;
}

@Injectable({ providedIn: 'root' })
export class LeaveService {
  private readonly base = `/${CONTROLLER_CONSTANTS.LEAVE}`;

  constructor(private baseHttp: BaseHttpService) {}

  getBalance(): Observable<ApiResponse<LeaveBalance>> {
    return this.baseHttp.get<ApiResponse<LeaveBalance>>(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_BALANCE}`
    );
  }

  getMyRequests(): Observable<ApiResponse<LeaveRequest[]>> {
    return this.baseHttp.get<ApiResponse<LeaveRequest[]>>(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_MY_REQUESTS}`
    );
  }

  getTeamRequests(): Observable<ApiResponse<LeaveRequest[]>> {
    return this.baseHttp.get<ApiResponse<LeaveRequest[]>>(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_TEAM_REQUESTS}`
    );
  }

  getMySystemRole(): Observable<ApiResponse<LeaveSystemRoleInfo>> {
    return this.baseHttp.get<ApiResponse<LeaveSystemRoleInfo>>(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_ME_SYSTEM_ROLE}`
    );
  }

  apply(payload: ApplyLeavePayload): Observable<ApiResponse<LeaveRequest>> {
    const form = new FormData();
    form.append('LeaveType', payload.leaveType);
    form.append('StartDate', payload.startDate);
    form.append('EndDate', payload.endDate);
    form.append('IsHalfDay', String(payload.isHalfDay));
    if (payload.isHalfDay && payload.halfDayPeriod) {
      form.append('HalfDayPeriod', payload.halfDayPeriod);
    }
    form.append('Reason', payload.reason);
    if (payload.attachment) {
      form.append('attachment', payload.attachment, payload.attachment.name);
    }
    return this.baseHttp.postFile<ApiResponse<LeaveRequest>>(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_REQUESTS}`,
      form
    );
  }

  cancel(id: string): Observable<ApiResponse<LeaveRequest>> {
    return this.baseHttp.post<ApiResponse<LeaveRequest>>(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_REQUESTS}/${id}/Cancel`,
      {}
    );
  }

  approve(id: string): Observable<ApiResponse<LeaveRequest>> {
    return this.baseHttp.post<ApiResponse<LeaveRequest>>(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_REQUESTS}/${id}/Approve`,
      {}
    );
  }

  reject(id: string, rejectionReason: string): Observable<ApiResponse<LeaveRequest>> {
    return this.baseHttp.post<ApiResponse<LeaveRequest>>(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_REQUESTS}/${id}/Reject`,
      { rejectionReason }
    );
  }

  getAttachment(id: string): Observable<HttpResponse<Blob>> {
    return this.baseHttp.getBlobWithResponse(
      `${this.base}/${ENDPOINT_CONSTANTS.LEAVE_REQUESTS}/${id}/${ENDPOINT_CONSTANTS.LEAVE_ATTACHMENT}`
    );
  }
}
